import { html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LandiStorefrontElement } from '../base/landi-storefront-element.js';
import { sharedStyles, buttonStyles, inputStyles, layoutStyles, typographyStyles } from '../styles/shared.js';
import { storeApi, resolveProductImageUrl, normalizeProductCategory, productCategoriesMatch, type Product } from '@landi/store-core';
import './landi-product-card.js';

type SortOption = 'featured' | 'price-asc' | 'price-desc' | 'name-asc';
type ViewMode = 'grid' | 'list';
type PageItem = number | 'ellipsis';

const PAGE_SIZE_OPTIONS = [12, 24, 48] as const;
type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];
const DEFAULT_PAGE_SIZE: PageSizeOption = 24;

interface PriceRange {
  min: number;
  max: number;
}

function getVisiblePageNumbers(current: number, total: number): PageItem[] {
  if (total <= 0) {
    return [];
  }
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages: PageItem[] = [1];
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);

  if (windowStart > 2) {
    pages.push('ellipsis');
  }

  for (let page = windowStart; page <= windowEnd; page += 1) {
    pages.push(page);
  }

  if (windowEnd < total - 1) {
    pages.push('ellipsis');
  }

  pages.push(total);
  return pages;
}

/**
 * Full catalog page with sidebar filters, sort, search results, and product grid.
 *
 * @element landi-product-catalog
 */
@customElement('landi-product-catalog')
export class LandiProductCatalog extends LandiStorefrontElement {
  static styles = [
    sharedStyles,
    buttonStyles,
    inputStyles,
    layoutStyles,
    typographyStyles,
    css`
      :host {
        display: block;
        background: var(--landi-background);
        min-height: 60vh;
      }

      .catalog-container {
        max-width: 80rem;
        margin: 0 auto;
        padding: 0 1.5rem;
      }

      @media (min-width: 640px) {
        .catalog-container {
          padding: 0 3rem;
        }
      }

      .breadcrumbs {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 1.25rem 0 0.75rem;
        font-size: 0.813rem;
        color: var(--landi-text-muted);
      }

      .breadcrumbs a {
        color: var(--landi-text-muted);
        text-decoration: none;
        transition: color var(--landi-transition);
      }

      .breadcrumbs a:hover {
        color: var(--landi-primary);
      }

      .breadcrumbs .sep {
        color: var(--landi-text-light);
      }

      .breadcrumbs .current {
        color: var(--landi-text);
        font-weight: 500;
      }

      .catalog-layout {
        display: grid;
        grid-template-columns: 1fr;
        gap: 2rem;
        padding-bottom: 4rem;
      }

      @media (min-width: 1024px) {
        .catalog-layout {
          grid-template-columns: 240px 1fr;
          gap: 2.5rem;
        }
      }

      /* Sidebar */
      .sidebar {
        display: none;
      }

      @media (min-width: 1024px) {
        .sidebar {
          display: block;
        }
      }

      .sidebar-panel {
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-lg);
        padding: 1.5rem;
      }

      .filter-section + .filter-section {
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px solid var(--landi-border-light);
      }

      .filter-heading {
        font-size: 0.688rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--landi-text);
        margin: 0 0 1rem;
      }

      .price-inputs {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }

      .price-inputs input {
        flex: 1;
        min-width: 0;
        padding: 0.5rem 0.625rem;
        font-size: 0.813rem;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        background: var(--landi-background);
        color: var(--landi-text);
      }

      .price-inputs input:focus {
        outline: none;
        border-color: var(--landi-primary);
        box-shadow: 0 0 0 2px var(--landi-primary-light);
      }

      .price-sep {
        color: var(--landi-text-light);
        font-size: 0.75rem;
      }

      .category-list {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
      }

      .category-btn {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0.5rem 0.75rem;
        font-size: 0.813rem;
        color: var(--landi-text-muted);
        background: transparent;
        border: none;
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: all var(--landi-transition);
        text-align: left;
      }

      .category-btn:hover {
        background: var(--landi-background);
        color: var(--landi-text);
      }

      .category-btn.active {
        background: var(--landi-primary-light);
        color: var(--landi-primary);
        font-weight: 600;
      }

      .category-count {
        font-size: 0.75rem;
        color: var(--landi-text-light);
      }

      .tag-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.375rem;
      }

      .tag-chip {
        padding: 0.375rem 0.75rem;
        font-size: 0.75rem;
        color: var(--landi-text-muted);
        background: var(--landi-background);
        border: 1px solid var(--landi-border);
        border-radius: 999px;
        cursor: pointer;
        transition: all var(--landi-transition);
      }

      .tag-chip:hover {
        border-color: var(--landi-primary);
        color: var(--landi-primary);
      }

      .tag-chip.active {
        background: var(--landi-primary);
        border-color: var(--landi-primary);
        color: var(--landi-surface);
      }

      .checkbox-row {
        display: flex;
        align-items: center;
        gap: 0.625rem;
        cursor: pointer;
        font-size: 0.813rem;
        color: var(--landi-text-muted);
      }

      .checkbox-row input {
        width: 16px;
        height: 16px;
        accent-color: var(--landi-primary);
        cursor: pointer;
      }

      /* Mobile filter drawer */
      .mobile-filter-bar {
        display: flex;
        gap: 0.75rem;
        margin-bottom: 1rem;
      }

      @media (min-width: 1024px) {
        .mobile-filter-bar {
          display: none;
        }
      }

      .filter-drawer-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem 1rem;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--landi-text);
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
      }

      .filter-drawer-btn svg {
        width: 16px;
        height: 16px;
      }

      .drawer-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        z-index: 300;
        opacity: 0;
        visibility: hidden;
        transition: opacity 300ms ease, visibility 300ms ease;
      }

      .drawer-overlay.open {
        opacity: 1;
        visibility: visible;
      }

      .drawer-panel {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        width: min(320px, 90vw);
        background: var(--landi-surface);
        z-index: 301;
        transform: translateX(-100%);
        transition: transform 300ms ease;
        overflow-y: auto;
        padding: 1.5rem;
        box-shadow: var(--landi-shadow-xl);
      }

      .drawer-panel.open {
        transform: translateX(0);
      }

      .drawer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.5rem;
      }

      .drawer-title {
        font-family: var(--landi-font-serif);
        font-size: 1.25rem;
        font-weight: 500;
        margin: 0;
      }

      .drawer-close {
        padding: 0.375rem;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--landi-text-muted);
      }

      /* Main content */
      .catalog-main {
        min-width: 0;
      }

      .catalog-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 1.5rem;
        padding-bottom: 1.25rem;
        border-bottom: 1px solid var(--landi-border-light);
      }

      .results-header {
        flex: 1;
        min-width: 0;
      }

      .results-title {
        font-family: var(--landi-font-serif);
        font-size: 1.75rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 0.25rem;
        line-height: 1.2;
      }

      .results-count {
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        margin: 0;
      }

      .toolbar-actions {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .sort-select {
        padding: 0.5rem 2rem 0.5rem 0.75rem;
        font-size: 0.813rem;
        color: var(--landi-text);
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 0.625rem center;
      }

      .sort-select:focus {
        outline: none;
        border-color: var(--landi-primary);
        box-shadow: 0 0 0 2px var(--landi-primary-light);
      }

      .view-toggle {
        display: flex;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        overflow: hidden;
      }

      .view-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        padding: 0;
        background: var(--landi-surface);
        border: none;
        color: var(--landi-text-muted);
        cursor: pointer;
        transition: all var(--landi-transition);
      }

      .view-btn + .view-btn {
        border-left: 1px solid var(--landi-border);
      }

      .view-btn:hover {
        color: var(--landi-primary);
      }

      .view-btn.active {
        background: var(--landi-text);
        color: var(--landi-surface);
      }

      .view-btn svg {
        width: 18px;
        height: 18px;
      }

      /* Active filter chips */
      .active-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 1.25rem;
      }

      .active-chip {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.375rem 0.75rem;
        font-size: 0.75rem;
        color: var(--landi-text);
        background: var(--landi-primary-light);
        border: 1px solid rgba(180, 147, 42, 0.25);
        border-radius: 999px;
      }

      .active-chip button {
        display: flex;
        padding: 0;
        background: transparent;
        border: none;
        color: var(--landi-primary);
        cursor: pointer;
        line-height: 1;
      }

      .clear-all-btn {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--landi-primary);
        background: transparent;
        border: none;
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      .clear-all-btn:hover {
        color: var(--landi-primary-hover);
      }

      /* Product grid / list */
      .products-grid {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        gap: 1.5rem;
      }

      @media (min-width: 640px) {
        .products-grid.grid-view {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (min-width: 1280px) {
        .products-grid.grid-view {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      .products-grid.list-view {
        grid-template-columns: 1fr;
        gap: 1rem;
      }

      .products-grid.list-view landi-product-card {
        --card-list-mode: 1;
      }

      .empty-state {
        text-align: center;
        padding: 4rem 2rem;
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-lg);
      }

      .empty-state h3 {
        font-family: var(--landi-font-serif);
        font-size: 1.5rem;
        color: var(--landi-text);
        margin: 0 0 0.5rem;
      }

      .empty-state p {
        color: var(--landi-text-muted);
        margin: 0 0 1.5rem;
      }

      .loading-state {
        text-align: center;
        padding: 4rem 2rem;
        color: var(--landi-text-muted);
      }

      .catalog-pagination {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        margin-top: 2rem;
        padding-top: 1.5rem;
        border-top: 1px solid var(--landi-border-light);
      }

      .pagination-summary {
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        margin: 0;
      }

      .pagination-controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;
      }

      .page-size-label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.813rem;
        color: var(--landi-text-muted);
      }

      .page-size-select {
        padding: 0.5rem 2rem 0.5rem 0.75rem;
        font-size: 0.813rem;
        color: var(--landi-text);
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 0.625rem center;
      }

      .page-size-select:focus {
        outline: none;
        border-color: var(--landi-primary);
        box-shadow: 0 0 0 2px var(--landi-primary-light);
      }

      .page-nav {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.375rem;
      }

      .page-btn {
        min-width: 36px;
        height: 36px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 0.625rem;
        font-size: 0.813rem;
        font-weight: 500;
        color: var(--landi-text-muted);
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: all var(--landi-transition);
      }

      .page-btn:hover:not(:disabled) {
        border-color: var(--landi-primary);
        color: var(--landi-primary);
      }

      .page-btn.active {
        background: var(--landi-text);
        border-color: var(--landi-text);
        color: var(--landi-surface);
      }

      .page-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .page-btn.nav-btn {
        padding: 0 0.875rem;
      }

      .page-ellipsis {
        min-width: 24px;
        text-align: center;
        font-size: 0.813rem;
        color: var(--landi-text-light);
      }

      @media (max-width: 639px) {
        .catalog-pagination {
          flex-direction: column;
          align-items: stretch;
        }

        .pagination-controls {
          flex-direction: column;
          align-items: stretch;
        }

        .page-nav {
          justify-content: center;
        }

        .page-size-label {
          justify-content: space-between;
        }
      }
    `,
  ];

  @property({ type: String, attribute: 'product-href-base' }) productHrefBase = 'product.html?id=';
  @property({ type: String, attribute: 'home-href' }) homeHref = 'index.html';
  @property({ type: String, attribute: 'search-query' }) searchQuery = '';
  /** When set, omit breadcrumbs and page title — host page supplies v1.2 hero chrome. */
  @property({ type: Boolean, attribute: 'hide-header' }) hideHeader = false;

  @state() private allProducts: Product[] = [];
  @state() private loading = true;
  @state() private loadError = '';
  @state() private selectedCategory = '';
  @state() private selectedTags: string[] = [];
  @state() private inStockOnly = true;
  @state() private priceMin = '';
  @state() private priceMax = '';
  @state() private sortBy: SortOption = 'featured';
  @state() private viewMode: ViewMode = 'grid';
  @state() private drawerOpen = false;
  @state() private currentPage = 1;
  @state() private pageSize: PageSizeOption = DEFAULT_PAGE_SIZE;

  connectedCallback() {
    super.connectedCallback();
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlQuery = params.get('q')?.trim();
      if (!this.searchQuery && urlQuery) {
        this.searchQuery = urlQuery;
      }

      const pageParam = params.get('page');
      if (pageParam) {
        const parsedPage = Number.parseInt(pageParam, 10);
        if (!Number.isNaN(parsedPage) && parsedPage >= 1) {
          this.currentPage = parsedPage;
        }
      }

      const inStockParam = params.get('inStock');
      if (inStockParam !== null) {
        this.inStockOnly = inStockParam !== '0' && inStockParam.toLowerCase() !== 'false';
      }
    }
    void this.loadProducts();
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
      this.syncUrlParams();
    }
  }

  private get activeSearchQuery(): string {
    return this.searchQuery.trim();
  }

  private async loadProducts() {
    this.loading = true;
    this.loadError = '';
    try {
      if (this.activeSearchQuery) {
        this.allProducts = await storeApi.searchProducts(this.activeSearchQuery);
      } else {
        this.allProducts = await storeApi.getProducts();
      }
    } catch (err) {
      this.loadError = err instanceof Error ? err.message : 'Failed to load products';
      this.allProducts = [];
    } finally {
      this.loading = false;
    }
  }

  private get categories(): Array<{ name: string; count: number }> {
    const counts = new Map<string, { name: string; count: number }>();
    for (const product of this.allProducts) {
      const name = normalizeProductCategory(product.category);
      if (!name) {
        continue;
      }
      const key = name.toLowerCase();
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, { name, count: 1 });
      }
    }
    return Array.from(counts.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  private get availableTags(): Array<{ name: string; count: number }> {
    const counts = new Map<string, number>();
    for (const product of this.allProducts) {
      for (const tag of product.tags ?? []) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private get priceBounds(): PriceRange {
    if (this.allProducts.length === 0) {
      return { min: 0, max: 0 };
    }
    const prices = this.allProducts.map((p) => p.price);
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }

  private get filteredProducts(): Product[] {
    let results = [...this.allProducts];

    if (this.selectedCategory) {
      results = results.filter((p) => productCategoriesMatch(p.category, this.selectedCategory));
    }

    if (this.selectedTags.length > 0) {
      results = results.filter((p) => {
        const tags = p.tags ?? [];
        return this.selectedTags.every((t) => tags.includes(t));
      });
    }

    if (this.inStockOnly) {
      results = results.filter((p) => p.inventoryQuantity > 0);
    }

    const minVal = parseFloat(this.priceMin);
    if (!Number.isNaN(minVal) && this.priceMin !== '') {
      results = results.filter((p) => p.price >= minVal);
    }

    const maxVal = parseFloat(this.priceMax);
    if (!Number.isNaN(maxVal) && this.priceMax !== '') {
      results = results.filter((p) => p.price <= maxVal);
    }

    return this.sortProducts(results);
  }

  private get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredProducts.length / this.pageSize));
  }

  private get effectivePage(): number {
    return Math.min(this.currentPage, this.totalPages);
  }

  private get paginatedProducts(): Product[] {
    const start = (this.effectivePage - 1) * this.pageSize;
    return this.filteredProducts.slice(start, start + this.pageSize);
  }

  private get resultsRangeStart(): number {
    if (this.filteredProducts.length === 0) {
      return 0;
    }
    return (this.effectivePage - 1) * this.pageSize + 1;
  }

  private get resultsRangeEnd(): number {
    return Math.min(this.effectivePage * this.pageSize, this.filteredProducts.length);
  }

  private syncUrlParams() {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams();
    if (this.activeSearchQuery) {
      params.set('q', this.activeSearchQuery);
    }
    if (this.currentPage > 1) {
      params.set('page', String(this.currentPage));
    }

    const query = params.toString();
    const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState(null, '', nextUrl);
  }

  private resetToFirstPage() {
    if (this.currentPage !== 1) {
      this.currentPage = 1;
      this.syncUrlParams();
    }
  }

  private goToPage(page: number) {
    const clamped = Math.min(Math.max(page, 1), this.totalPages);
    if (clamped === this.currentPage) {
      return;
    }
    this.currentPage = clamped;
    this.syncUrlParams();
    this.scrollToResultsTop();
  }

  private setPageSize(size: PageSizeOption) {
    if (this.pageSize === size) {
      return;
    }
    this.pageSize = size;
    this.resetToFirstPage();
  }

  private scrollToResultsTop() {
    const toolbar = this.renderRoot.querySelector('.catalog-toolbar');
    if (toolbar instanceof HTMLElement) {
      toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private handlePageSizeChange(e: Event) {
    const value = Number.parseInt((e.target as HTMLSelectElement).value, 10);
    if (PAGE_SIZE_OPTIONS.includes(value as PageSizeOption)) {
      this.setPageSize(value as PageSizeOption);
    }
  }

  private sortProducts(products: Product[]): Product[] {
    const sorted = [...products];
    switch (this.sortBy) {
      case 'price-asc':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return sorted.sort((a, b) => b.price - a.price);
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'featured':
      default:
        return sorted.sort((a, b) => {
          const aFeatured = a.featured ? 1 : 0;
          const bFeatured = b.featured ? 1 : 0;
          if (aFeatured !== bFeatured) return bFeatured - aFeatured;
          return a.name.localeCompare(b.name);
        });
    }
  }

  private get hasActiveFilters(): boolean {
    return (
      this.selectedCategory !== '' ||
      this.selectedTags.length > 0 ||
      this.inStockOnly ||
      this.priceMin !== '' ||
      this.priceMax !== ''
    );
  }

  private selectCategory(category: string) {
    this.selectedCategory = category;
    this.resetToFirstPage();
  }

  private toggleTag(tag: string) {
    if (this.selectedTags.includes(tag)) {
      this.selectedTags = this.selectedTags.filter((t) => t !== tag);
    } else {
      this.selectedTags = [...this.selectedTags, tag];
    }
    this.resetToFirstPage();
  }

  private clearAllFilters() {
    this.selectedCategory = '';
    this.selectedTags = [];
    this.inStockOnly = false;
    this.priceMin = '';
    this.priceMax = '';
    this.resetToFirstPage();
  }

  private openDrawer() {
    this.drawerOpen = true;
  }

  private closeDrawer() {
    this.drawerOpen = false;
  }

  private handlePriceMinInput(e: Event) {
    this.priceMin = (e.target as HTMLInputElement).value;
    this.resetToFirstPage();
  }

  private handlePriceMaxInput(e: Event) {
    this.priceMax = (e.target as HTMLInputElement).value;
    this.resetToFirstPage();
  }

  private handleSortChange(e: Event) {
    this.sortBy = (e.target as HTMLSelectElement).value as SortOption;
    this.resetToFirstPage();
  }

  private getMetadataString(product: Product, key: string): string {
    const value = product.metadata?.[key];
    return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
  }

  private getMetadataNumber(product: Product, key: string): number {
    const value = product.metadata?.[key];
    return typeof value === 'number' ? value : 0;
  }

  private renderFilterPanel() {
    const bounds = this.priceBounds;

    return html`
      <div class="filter-section">
        <h3 class="filter-heading">Price Range</h3>
        <div class="price-inputs">
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder=${bounds.min > 0 ? String(bounds.min) : 'Min'}
            .value=${this.priceMin}
            @input=${this.handlePriceMinInput}
            aria-label="Minimum price"
          />
          <span class="price-sep">–</span>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder=${bounds.max > 0 ? String(bounds.max) : 'Max'}
            .value=${this.priceMax}
            @input=${this.handlePriceMaxInput}
            aria-label="Maximum price"
          />
        </div>
      </div>

      ${this.categories.length > 0 ? html`
        <div class="filter-section">
          <h3 class="filter-heading">Category</h3>
          <div class="category-list">
            <button
              class="category-btn ${!this.selectedCategory ? 'active' : ''}"
              @click=${() => this.selectCategory('')}
            >
              <span>All Categories</span>
              <span class="category-count">${this.allProducts.length}</span>
            </button>
            ${this.categories.map((cat) => html`
              <button
                class="category-btn ${this.selectedCategory === cat.name ? 'active' : ''}"
                @click=${() => this.selectCategory(cat.name)}
              >
                <span>${cat.name}</span>
                <span class="category-count">${cat.count}</span>
              </button>
            `)}
          </div>
        </div>
      ` : nothing}

      ${this.availableTags.length > 0 ? html`
        <div class="filter-section">
          <h3 class="filter-heading">Tags</h3>
          <div class="tag-chips">
            ${this.availableTags.map((tag) => html`
              <button
                class="tag-chip ${this.selectedTags.includes(tag.name) ? 'active' : ''}"
                @click=${() => this.toggleTag(tag.name)}
              >
                ${tag.name}
              </button>
            `)}
          </div>
        </div>
      ` : nothing}

      <div class="filter-section">
        <label class="checkbox-row">
          <input
            type="checkbox"
            .checked=${this.inStockOnly}
            @change=${(e: Event) => {
              this.inStockOnly = (e.target as HTMLInputElement).checked;
              this.resetToFirstPage();
            }}
          />
          <span>In stock only</span>
        </label>
      </div>
    `;
  }

  private renderActiveChips() {
    if (!this.hasActiveFilters) return nothing;

    const chips: Array<{ label: string; clear: () => void }> = [];

    if (this.selectedCategory) {
      chips.push({
        label: `Category: ${this.selectedCategory}`,
        clear: () => {
          this.selectedCategory = '';
          this.resetToFirstPage();
        },
      });
    }

    for (const tag of this.selectedTags) {
      chips.push({
        label: `Tag: ${tag}`,
        clear: () => {
          this.selectedTags = this.selectedTags.filter((t) => t !== tag);
          this.resetToFirstPage();
        },
      });
    }

    if (this.inStockOnly) {
      chips.push({
        label: 'In stock',
        clear: () => {
          this.inStockOnly = false;
          this.resetToFirstPage();
        },
      });
    }

    if (this.priceMin !== '') {
      chips.push({
        label: `Min: $${this.priceMin}`,
        clear: () => {
          this.priceMin = '';
          this.resetToFirstPage();
        },
      });
    }

    if (this.priceMax !== '') {
      chips.push({
        label: `Max: $${this.priceMax}`,
        clear: () => {
          this.priceMax = '';
          this.resetToFirstPage();
        },
      });
    }

    return html`
      <div class="active-filters" aria-label="Active filters">
        ${chips.map((chip) => html`
          <span class="active-chip">
            ${chip.label}
            <button type="button" aria-label="Remove filter ${chip.label}" @click=${chip.clear}>×</button>
          </span>
        `)}
        <button type="button" class="clear-all-btn" @click=${this.clearAllFilters}>Clear all</button>
      </div>
    `;
  }

  private renderBreadcrumbs() {
    const isSearch = this.activeSearchQuery.length > 0;

    return html`
      <nav class="breadcrumbs" aria-label="Breadcrumb">
        <a href=${this.homeHref}>Home</a>
        <span class="sep">›</span>
        ${isSearch ? html`
          <a href="products.html">Products</a>
          <span class="sep">›</span>
          <span class="current">Search: "${this.activeSearchQuery}"</span>
        ` : html`
          <span class="current">Products</span>
        `}
      </nav>
    `;
  }

  private renderProductCard(product: Product) {
    return html`
      <landi-product-card
        product-id=${product.id}
        .product=${product}
        name=${product.name}
        price=${product.price}
        .comparePrice=${product.compareAtPrice}
        image=${resolveProductImageUrl(product.images[0]?.url) || ''}
        sku=${product.sku || ''}
        category=${product.category || ''}
        description=${product.shortDescription || ''}
        rating=${this.getMetadataNumber(product, 'rating')}
        reviews=${this.getMetadataNumber(product, 'reviews')}
        .inStock=${product.inventoryQuantity > 0}
        .featured=${product.featured ?? false}
        format=${this.getMetadataString(product, 'format')}
        purity=${this.getMetadataString(product, 'purity')}
        test-method=${this.getMetadataString(product, 'testMethod')}
        quantity=${product.variants?.[0]?.name || ''}
        href="${this.productHrefBase}${product.id}"
      ></landi-product-card>
    `;
  }

  private renderPagination(totalResults: number) {
    if (totalResults === 0) {
      return nothing;
    }

    const visiblePages = getVisiblePageNumbers(this.effectivePage, this.totalPages);

    return html`
      <div class="catalog-pagination" aria-label="Product pagination">
        <p class="pagination-summary">
          Showing ${this.resultsRangeStart}–${this.resultsRangeEnd} of ${totalResults} result${totalResults === 1 ? '' : 's'}
        </p>
        <div class="pagination-controls">
          <label class="page-size-label">
            Per page
            <select
              class="page-size-select"
              .value=${String(this.pageSize)}
              @change=${this.handlePageSizeChange}
              aria-label="Products per page"
            >
              ${PAGE_SIZE_OPTIONS.map((size) => html`
                <option value=${String(size)} ?selected=${this.pageSize === size}>${size}</option>
              `)}
            </select>
          </label>
          ${this.totalPages > 1 ? html`
            <nav class="page-nav" aria-label="Page navigation">
              <button
                type="button"
                class="page-btn nav-btn"
                ?disabled=${this.effectivePage <= 1}
                @click=${() => this.goToPage(this.effectivePage - 1)}
                aria-label="Previous page"
              >
                Previous
              </button>
              ${visiblePages.map((page) => page === 'ellipsis' ? html`
                <span class="page-ellipsis" aria-hidden="true">…</span>
              ` : html`
                <button
                  type="button"
                  class="page-btn ${this.effectivePage === page ? 'active' : ''}"
                  @click=${() => this.goToPage(page)}
                  aria-label="Page ${page}"
                  aria-current=${this.effectivePage === page ? 'page' : 'false'}
                >
                  ${page}
                </button>
              `)}
              <button
                type="button"
                class="page-btn nav-btn"
                ?disabled=${this.effectivePage >= this.totalPages}
                @click=${() => this.goToPage(this.effectivePage + 1)}
                aria-label="Next page"
              >
                Next
              </button>
            </nav>
          ` : nothing}
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="catalog-container">
          ${this.hideHeader ? nothing : this.renderBreadcrumbs()}
          <div class="loading-state">Loading products…</div>
        </div>
      `;
    }

    if (this.loadError) {
      return html`
        <div class="catalog-container">
          ${this.hideHeader ? nothing : this.renderBreadcrumbs()}
          <div class="empty-state">
            <h3>Unable to load products</h3>
            <p>${this.loadError}</p>
          </div>
        </div>
      `;
    }

    const products = this.filteredProducts;
    const visibleProducts = this.paginatedProducts;
    const isSearch = this.activeSearchQuery.length > 0;
    const resultsTitle = isSearch
      ? `Results for "${this.activeSearchQuery}"`
      : 'All Products';

    return html`
      <div class="catalog-container">
        ${this.hideHeader ? nothing : this.renderBreadcrumbs()}

        <div class="mobile-filter-bar">
          <button type="button" class="filter-drawer-btn" @click=${this.openDrawer}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M4 6h16M4 12h10M4 18h6"></path>
            </svg>
            Filters
            ${this.hasActiveFilters ? html`<span>(${this.selectedTags.length + (this.selectedCategory ? 1 : 0) + (this.inStockOnly ? 1 : 0) + (this.priceMin ? 1 : 0) + (this.priceMax ? 1 : 0)})</span>` : nothing}
          </button>
        </div>

        <div
          class="drawer-overlay ${this.drawerOpen ? 'open' : ''}"
          @click=${this.closeDrawer}
          aria-hidden=${!this.drawerOpen}
        ></div>
        <aside
          class="drawer-panel ${this.drawerOpen ? 'open' : ''}"
          aria-label="Product filters"
          aria-hidden=${!this.drawerOpen}
        >
          <div class="drawer-header">
            <h2 class="drawer-title">Filters</h2>
            <button type="button" class="drawer-close" @click=${this.closeDrawer} aria-label="Close filters">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          ${this.renderFilterPanel()}
        </aside>

        <div class="catalog-layout">
          <aside class="sidebar" aria-label="Product filters">
            <div class="sidebar-panel">
              <h2 class="filter-heading" style="font-size: 0.875rem; margin-bottom: 1.25rem;">Filters</h2>
              ${this.renderFilterPanel()}
            </div>
          </aside>

          <main class="catalog-main">
            <div class="catalog-toolbar">
              ${this.hideHeader ? nothing : html`
                <div class="results-header">
                  <h1 class="results-title">${resultsTitle}</h1>
                  <p class="results-count">${products.length} result${products.length === 1 ? '' : 's'}</p>
                </div>
              `}
              <div class="toolbar-actions ${this.hideHeader ? 'toolbar-actions-only' : ''}">
                <label class="sr-only" for="sort-select">Sort products</label>
                <select
                  id="sort-select"
                  class="sort-select"
                  .value=${this.sortBy}
                  @change=${this.handleSortChange}
                >
                  <option value="featured">Featured</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="name-asc">Name: A–Z</option>
                </select>
                <div class="view-toggle" role="group" aria-label="View mode">
                  <button
                    type="button"
                    class="view-btn ${this.viewMode === 'grid' ? 'active' : ''}"
                    @click=${() => { this.viewMode = 'grid'; }}
                    aria-label="Grid view"
                    aria-pressed=${this.viewMode === 'grid'}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="view-btn ${this.viewMode === 'list' ? 'active' : ''}"
                    @click=${() => { this.viewMode = 'list'; }}
                    aria-label="List view"
                    aria-pressed=${this.viewMode === 'list'}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="8" y1="6" x2="21" y2="6"></line>
                      <line x1="8" y1="12" x2="21" y2="12"></line>
                      <line x1="8" y1="18" x2="21" y2="18"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            ${this.renderActiveChips()}

            ${products.length > 0 ? html`
              <div class="products-grid ${this.viewMode === 'list' ? 'list-view' : 'grid-view'}">
                ${visibleProducts.map((product) => this.renderProductCard(product))}
              </div>
              ${this.renderPagination(products.length)}
            ` : html`
              <div class="empty-state">
                <h3>No products found</h3>
                <p>Try adjusting your filters or search terms.</p>
                ${this.hasActiveFilters ? html`
                  <button type="button" class="btn btn-secondary" @click=${this.clearAllFilters}>
                    Clear Filters
                  </button>
                ` : nothing}
              </div>
            `}
          </main>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-catalog': LandiProductCatalog;
  }
}
